import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableColumnsReordererComponent } from '@rs-shared-lib/components/table-columns-reorderer/table-columns-reorderer.component';
import { MatCell, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { RsTypeSafeMatCellDef } from '@rs-shared-lib/directives/type-safe-mat-cell-def/type-safe-mat-cell-def.directive';
import { BatchService } from '@batch/shared/services/batch.service';
import { Batch } from '@batch/shared/models/batch';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { MatButton, MatIconButton } from '@angular/material/button';
import { RsNullDashPipe } from '@rs-shared-lib/pipes/rs-null-dash/rs-null-dash.pipe';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UploadFileDialogComponent } from '@batch/dialogs/upload-file/upload-file-dialog.component';
import { Store } from '@ngrx/store';
import { selectBatchFilters } from '@batch/state/batch.selectors';
import { BatchActions } from '@batch/state/batch.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RsSpinnerButtonDirective } from '@rs-shared-lib/directives/spinner-button/rs-spinner-button.directive';
import { RsLoaderService } from '@rs-shared-lib/components/rs-loader/services/rs-loader.service';
import { UUID } from '@rs-shared-lib/models';
import { rsDownloadDocumentHandler } from '@rs-shared-lib/helpers/download-document-handler';
import { RsSubHeaderComponent } from '@rs-shared-lib/components/subheader/rs-sub-header.component';
import { CancelProcessComponent } from '@batch/dialogs/cancel-process/cancel-process/cancel-process.component';
import { StatusChipClassPipe } from '@batch/pipes/status-chip.pipe';

@Component({
  selector: 'ord-admin-batch',
  imports: [
    CommonModule,
    TableColumnsReordererComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    TranslateModule,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatPaginator,
    MatHeaderCellDef,
    MatCell,
    RsTypeSafeMatCellDef,
    MatIcon,
    MatTooltip,
    MatIconButton,
    RsNullDashPipe,
    RsSpinnerButtonDirective,
    MatDialogModule,
    MatButton,
    RsSubHeaderComponent,
    StatusChipClassPipe
  ],
  templateUrl: './batch.component.html',
  styleUrls: ['./batch.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BatchComponent {
  protected readonly tableColumns = signal(['STATUS', 'DATE', 'FILE', 'RESULT_FILE', 'ACTIONS']);
  protected readonly dataSet$: Observable<PageDataset>;

  private readonly store = inject(Store);

  protected paginationParams = this.store.selectSignal(selectBatchFilters)();
  private readonly refreshDataset$ = new BehaviorSubject<void>(undefined);

  constructor(
    protected readonly translateService: TranslateService,
    private readonly batchService: BatchService,
    private readonly spinnerService: RsLoaderService,
    private readonly dialog: MatDialog
  ) {
    this.dataSet$ = this.refreshDataset$.pipe(
      tap(() => this.spinnerService.show()),
      switchMap(() => this.batchService.getBatches(this.store.selectSignal(selectBatchFilters)()).pipe(
        map(res => ({
          matTableDatasource: new MatTableDataSource(res.results),
          totalResults: res.totalResults
        })),
        catchError(() => EMPTY),
        finalize(() => this.spinnerService.hide())
      )),
      takeUntilDestroyed()
    );
  }

  public downloadInputFile(
    batchId: UUID,
    rsSpinnerButton: RsSpinnerButtonDirective
  ): void {
    rsSpinnerButton.rsSpinnerButtonShow.set(true);
    this.batchService.downloadBatchInputFile(batchId).pipe(
      finalize(() => rsSpinnerButton.rsSpinnerButtonShow.set(false))
    ).subscribe(file => rsDownloadDocumentHandler(file.blob, file.fileName));
  }

  public downloadResultFile(
    batchId: UUID,
    rsSpinnerButton: RsSpinnerButtonDirective
  ): void {
    rsSpinnerButton.rsSpinnerButtonShow.set(true);
    this.batchService.downloadBatchResultFile(batchId).pipe(
      finalize(() => rsSpinnerButton.rsSpinnerButtonShow.set(false))
    ).subscribe(( {
      blob, fileName
    } ) => rsDownloadDocumentHandler(blob, fileName));
  }

  public handlePageEvent( $event: PageEvent ): void {
    this.store.dispatch(BatchActions.updateTablePaginator({
      tablePagination: {
        _pageSize: $event.pageSize,
        _page: $event.pageIndex + 1
      }
    }));

    this.refreshDataset$.next();
  }

  public openDialog(): void {
    this.dialog.open(UploadFileDialogComponent, {
      width: '780px',
      autoFocus: false
    }).afterClosed().subscribe(( canReload: boolean ) => canReload && this.refreshDataset$.next());
  }

  public openConfirmDialog( batchId: UUID ): void {
    const dialogRef = this.dialog.open(CancelProcessComponent, {
      width: '780px'
    });

    dialogRef.afterClosed().pipe(
      switchMap(( canCancel: boolean ) =>
        canCancel
          ? this.batchService.cancelBatch(batchId).pipe(tap(() => this.refreshDataset$.next()))
          : of(null))
    ).subscribe();
  }
}

interface PageDataset {
  matTableDatasource: MatTableDataSource<Batch>;
  totalResults: number;
}
